import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Lightbulb, Sparkles } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { cn } from "../lib/utils";
import {
  LESSONS,
  REALITY_NOTES,
  PROGRESS_KEY,
  loadProgress,
} from "../lib/sewingCurriculum";
import { labHref } from "../lib/sewingLinks";
import SewingShell from "../components/sewing/SewingShell";

/* ============================================================
   /sewing-primitives/learn — the curriculum room: the lessons
   with their progress checkboxes (persisted in localStorage),
   plus the fabric-reality footnotes. Lessons with a shape
   deep-link into the lab.
   ============================================================ */

const LessonCard = ({ lesson, index, progress, onToggle }) => {
  const done = lesson.checks.filter((c) => progress[c.id]).length;
  return (
    <Card className="bg-card/60 backdrop-blur">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
            {index}
          </span>
          <CardTitle className="text-lg">{lesson.title}</CardTitle>
          {lesson.shape && (
            <Link
              to={labHref(lesson.shape)}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <ArrowUpRight className="h-3 w-3" />
              open in lab
            </Link>
          )}
        </div>
        <CardDescription>
          <span className="font-medium text-primary">Goal: </span>
          {lesson.goal}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed">
        {lesson.body.map((para, i) => (
          <p key={i} className="text-muted-foreground">
            {para}
          </p>
        ))}
        <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
            <Lightbulb className="h-3.5 w-3.5" />
            try this
          </p>
          <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
            {lesson.tryThis.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            checks · {done}/{lesson.checks.length}
          </p>
          {lesson.checks.map((check) => (
            <label
              key={check.id}
              htmlFor={check.id}
              className="flex cursor-pointer items-start gap-3 py-1 leading-relaxed"
            >
              <Checkbox
                id={check.id}
                checked={Boolean(progress[check.id])}
                onCheckedChange={() => onToggle(check.id)}
                className="mt-0.5"
              />
              <span className={cn(progress[check.id] && "text-muted-foreground line-through")}>
                {check.text}
              </span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const SewingLearn = () => {
  const [progress, setProgress] = useState(loadProgress);

  useEffect(() => {
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
    } catch {
      // Private-mode browsers may refuse; progress just won't persist.
    }
  }, [progress]);

  const toggleCheck = useCallback((id) => {
    setProgress((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const totalChecks = LESSONS.reduce((n, l) => n + l.checks.length, 0);
  const doneChecks = LESSONS.reduce(
    (n, l) => n + l.checks.filter((c) => progress[c.id]).length,
    0
  );

  return (
    <SewingShell
      active="learn"
      title="Learn"
      intro={
        <p className="mt-4 max-w-2xl text-muted-foreground">
          {LESSONS.length} lessons from "what is a seam" to "plush anything,"
          in dependency order. Checks save on this device. Lessons with a
          shape link straight into the lab.
        </p>
      }
    >
      <section className="mt-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <BookOpen className="h-6 w-6 text-primary" />
            The curriculum
          </h2>
          <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {doneChecks}/{totalChecks} checks done
          </span>
        </div>
        <div className="mt-6 space-y-5">
          {LESSONS.map((lesson, i) => (
            <LessonCard
              key={lesson.id}
              lesson={lesson}
              index={i}
              progress={progress}
              onToggle={toggleCheck}
            />
          ))}
        </div>
      </section>

      {/* ---- fabric reality ---- */}
      <section className="mt-16">
        <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Sparkles className="h-6 w-6 text-primary" />
          Where fabric disagrees with math
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          The geometry is exact; fabric is a negotiation. These are the
          standing corrections.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {REALITY_NOTES.map((note) => (
            <div key={note.title} className="rounded-xl border bg-card/50 p-4 backdrop-blur">
              <h3 className="text-sm font-semibold">{note.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {note.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <p className="mt-12 text-center text-sm text-muted-foreground">
        Graduated?{" "}
        <Link
          to="/sewing-primitives/make"
          className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
        >
          The make page has seven starter objects and a creature generator
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
        . 🧵
      </p>
    </SewingShell>
  );
};

export default SewingLearn;
